import React, { useEffect, useState } from 'react'

import Button from '@react/react-spectrum/Button'
import Dialog from '@react/react-spectrum/Dialog'
import ModalTrigger from '@react/react-spectrum/ModalTrigger'
import OverlayTrigger from '@react/react-spectrum/OverlayTrigger'
import Select from '@react/react-spectrum/Select'
import { Table, TBody, TD, TH, THead, TR } from '@react/react-spectrum/Table'
import Textfield from '@react/react-spectrum/Textfield'
import Tooltip from '@react/react-spectrum/Tooltip'

import Delete from '@react/react-spectrum/Icon/Delete'
import Edit from '@react/react-spectrum/Icon/Edit'
import Help from '@react/react-spectrum/Icon/Help'

import './ScoreEditor.scss'

const FIELD_TYPES = [
  {
    label: 'green',
    value: 'green',
  },
  {
    label: 'red',
    value: 'red',
  },
]

const NEW_FIELD = {
  type: FIELD_TYPES[0].value,
}

const renderWithTooltip = (label, tooltip) => (
  <>
    {label}{' '}
    <OverlayTrigger placement='right'>
      <Button className='tooltip-btn' variant='tool' icon={<Help />} />
      <Tooltip>{tooltip}</Tooltip>
    </OverlayTrigger>
  </>
)

export const ScoreEditor = ({ fields, disabled, onChange }) => {
  const [rows, setRows] = useState([])
  const hasEmptyRow = rows.length > 0 && rows.filter((r) => !r.title).length > 0

  const saveField = (index, propName) => (value) => {
    let _value = propName === 'name' ? value.replace(/(^[0-9]+|-|\s)/gm, '') : value
    const newRows = rows.map((r, idx) => (idx === index ? { ...r, [propName]: _value } : r))
    setRows(newRows)
    onChange(newRows)
  }

  const addRow = () => {
    if (!hasEmptyRow) {
      const newRows = [...rows, NEW_FIELD]
      setRows(newRows)
    }
  }

  const deleteRow = (index) => {
    const newRows = rows.filter((r, idx) => idx !== index)
    setRows(newRows)
    onChange(newRows)
  }

  useEffect(() => {
    setRows(fields || [])
  }, [fields])

  return (
    <div className='scoreEditor'>
      <Table onSubmit={(evt) => evt.preventDefault()}>
        <THead>
          <TH>Score name</TH>
          <TH>{renderWithTooltip('Score value', 'This can be a value or a path to the value.')}</TH>
          <TH>
            {renderWithTooltip('Max score', 'Handled only by the new Customer Experience 2 App. Defaults to 100')}
          </TH>
          <TH>
            {renderWithTooltip(
              'Score decimal points',
              'Handled only by the new Customer Experience 2 App. Leave empty for a default behavior of: 0 decimal points for max > 10, 1 decimal point for 1 < max <= 10, 2 decimal points for max <= 1'
            )}
          </TH>
          <TH>{renderWithTooltip('Score description', 'This can be a value or a path to the value.')}</TH>
          <TH>High score color</TH>
          <TH></TH>
        </THead>
        <TBody>
          {rows.map((field, idx) => (
            <TR key={'field-' + idx}>
              <TD>
                <Textfield value={field.title} onChange={saveField(idx, 'title')} disabled={disabled} />
              </TD>
              <TD>
                <Textfield value={field.attribute} onChange={saveField(idx, 'attribute')} disabled={disabled} />
              </TD>
              <TD>
                <Textfield value={field.max} onChange={saveField(idx, 'max')} disabled={disabled} />
              </TD>
              <TD>
                <Textfield value={field.decimalPoints} onChange={saveField(idx, 'decimalPoints')} disabled={disabled} />
              </TD>
              <TD>
                <Textfield value={field.description} onChange={saveField(idx, 'description')} disabled={disabled} />
              </TD>
              <TD>
                <Select
                  value={field.risk}
                  options={FIELD_TYPES}
                  onSelect={saveField(idx, 'risk')}
                  disabled={disabled}
                />
              </TD>
              <TD>
                <Button
                  variant='tool'
                  aria-label='Delete'
                  icon={<Delete />}
                  onClick={() => deleteRow(idx)}
                  disabled={disabled}
                />
              </TD>
            </TR>
          ))}
          <TR onClick={addRow}>
            <TD>
              <Button variant='action' quiet label='+ Add' disabled={disabled || hasEmptyRow} />
            </TD>
            <TD />
            <TD />
            <TD />
            <TD />
            <TD />
            <TD />
          </TR>
        </TBody>
      </Table>
    </div>
  )
}

export const ScoreEditorDialog = ({ label, fields, onChange, ...otherProps }) => {
  const [newContent, setNewContent] = useState(fields || [])
  return (
    <Dialog
      {...otherProps}
      className={'option-editor--dialog'}
      title={label || 'Option Editor'}
      confirmLabel='Save'
      cancelLabel='Discard'
      onConfirm={() => onChange(newContent)}
      role='dialog'
    >
      <ScoreEditor fields={newContent} onChange={setNewContent} />
    </Dialog>
  )
}

export const ScoreEditorInput = ({ label, fields, disabled, onChange }) => (
  <ModalTrigger>
    <Button icon={<Edit />}>Open Editor</Button>
    <ScoreEditorDialog label={label} fields={fields} onChange={onChange} disabled={disabled} />
  </ModalTrigger>
)
